// ============================================================================
// AdminBudgetList — sidebar landing page for budgets (UIF only).
// ============================================================================
// Loads every event (drafts + templates included) along with its budget
// totals, then renders a sortable / searchable table. Click "Open" on a row
// to navigate to the per-event budget panel at #admin/budgets/:id.
//
// Implementation note: there's no bulk budget endpoint, so we fetch each
// event's budget in parallel. For a UIF instance with dozens of events
// this is fine; for hundreds we'd want a backend rollup endpoint.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Search, RefreshCw, ArrowRight, ArrowUp, ArrowDown, DollarSign, Repeat as RepeatIcon,
} from 'lucide-react';
import { api, type PublicEvent } from '../api';

interface Props {
  token: string;
}

type SortKey = 'date' | 'title' | 'expected' | 'actual' | 'net';
type SortDir = 'asc' | 'desc';

interface Row {
  event: PublicEvent;
  expected_expense: number;
  actual_expense: number;
  expected_income: number;
  actual_income: number;
  net_expected: number;
  net_actual: number;
  line_count: number;
}

export function AdminBudgetList({ token }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'with-budget'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { events } = await api.events({
        includePast: true,
        includeTemplates: true,
        includeDrafts: true,
        token,
      });
      // Skip templates from the budget list — they're content holders.
      const realEvents = events.filter((e) => !e.is_recurring_template);
      // Fetch each event's budget in parallel.
      const results = await Promise.all(
        realEvents.map(async (ev) => {
          try {
            const b = await api.budget(token, ev.calendar_entry_id);
            const t = b.event_totals;
            return {
              event: ev,
              expected_expense: Number(t?.total_expected_expense ?? 0),
              actual_expense: Number(t?.total_actual_expense ?? 0),
              expected_income: Number(t?.total_expected_income ?? 0),
              actual_income: Number(t?.total_actual_income ?? 0),
              net_expected: Number(t?.net_expected ?? 0),
              net_actual: Number(t?.net_actual ?? 0),
              line_count: b.lines?.length ?? 0,
            } as Row;
          } catch {
            // If a single event's budget fails to load, still surface the row
            // with zeros so the admin can click in and investigate.
            return {
              event: ev,
              expected_expense: 0, actual_expense: 0,
              expected_income: 0, actual_income: 0,
              net_expected: 0, net_actual: 0,
              line_count: 0,
            } as Row;
          }
        }),
      );
      setRows(results);
    } catch (err: any) {
      setError(err?.message || 'Could not load budgets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Filter + sort
  const filtered = useMemo(() => {
    let r = rows;
    if (scope === 'with-budget') r = r.filter((row) => row.line_count > 0);
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter((row) =>
        row.event.title.toLowerCase().includes(q)
        || (row.event.series_label || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, scope, search]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const cmp = (a: Row, b: Row): number => {
      let av: string | number = 0;
      let bv: string | number = 0;
      switch (sortKey) {
        case 'title':    av = a.event.title.toLowerCase(); bv = b.event.title.toLowerCase(); break;
        case 'date':     av = new Date(a.event.start_date).getTime(); bv = new Date(b.event.start_date).getTime(); break;
        case 'expected': av = a.expected_expense; bv = b.expected_expense; break;
        case 'actual':   av = a.actual_expense;   bv = b.actual_expense;   break;
        case 'net':      av = a.net_actual;       bv = b.net_actual;       break;
      }
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    };
    list.sort(cmp);
    if (sortDir === 'desc') list.reverse();
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'date' ? 'desc' : 'asc'); }
  };

  // Grand totals across visible rows
  const totals = useMemo(() => sorted.reduce((acc, r) => ({
    expense: acc.expense + r.actual_expense,
    income:  acc.income  + r.actual_income,
    net:     acc.net     + r.net_actual,
  }), { expense: 0, income: 0, net: 0 }), [sorted]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budgets</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Per-event expenses, income, and LFPP / AMP / UIF funding splits.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
      )}

      {/* Grand totals across visible rows */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Actual expense" value={totals.expense} />
        <SummaryCard label="Actual income" value={totals.income} />
        <SummaryCard label="Net (actual)" value={totals.net} accent={totals.net >= 0 ? 'red' : 'green'} />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          <ScopeButton active={scope === 'all'}         onClick={() => setScope('all')}         label="All events" />
          <ScopeButton active={scope === 'with-budget'} onClick={() => setScope('with-budget')} label="With budget activity" />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[180px] relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or series…"
            className="flex-1 rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <SortableTh label="Event" activeKey={sortKey} dir={sortDir} sortKey="title" onClick={toggleSort} />
              <SortableTh label="Date"  activeKey={sortKey} dir={sortDir} sortKey="date"  onClick={toggleSort} />
              <SortableTh label="Expected expense" activeKey={sortKey} dir={sortDir} sortKey="expected" onClick={toggleSort} align="right" />
              <SortableTh label="Actual expense"   activeKey={sortKey} dir={sortDir} sortKey="actual"   onClick={toggleSort} align="right" />
              <SortableTh label="Net (actual)"     activeKey={sortKey} dir={sortDir} sortKey="net"      onClick={toggleSort} align="right" />
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No events match this view.</td></tr>
            )}
            {sorted.map((row) => (
              <tr key={row.event.calendar_entry_id} className="hover:bg-gray-50">
                <td className="px-4 py-2 align-top">
                  <div className="font-medium text-gray-900">{row.event.title}</div>
                  {row.event.series_label && (
                    <div className="text-xs text-gray-500 inline-flex items-center gap-1 mt-0.5">
                      <RepeatIcon className="h-3 w-3" />
                      {row.event.series_label}
                    </div>
                  )}
                  {row.line_count > 0 && (
                    <div className="text-[10px] text-gray-500 mt-0.5">{row.line_count} line{row.line_count === 1 ? '' : 's'}</div>
                  )}
                </td>
                <td className="px-4 py-2 align-top text-xs text-gray-700 whitespace-nowrap">
                  {new Date(row.event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                </td>
                <td className="px-4 py-2 align-top text-right font-mono">{money(row.expected_expense)}</td>
                <td className="px-4 py-2 align-top text-right font-mono">{money(row.actual_expense)}</td>
                <td className={'px-4 py-2 align-top text-right font-mono ' + (row.net_actual >= 0 ? 'text-red-700' : 'text-uif-green-dark')}>
                  {money(row.net_actual)}
                </td>
                <td className="px-4 py-2 align-top text-right">
                  <a
                    href={`#admin/budgets/${row.event.calendar_entry_id}`}
                    className="inline-flex items-center gap-1 text-xs text-uif-green-dark hover:underline"
                  >
                    Open <ArrowRight className="h-3 w-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function money(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function ScopeButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
        (active
          ? 'bg-uif-green text-white'
          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50')
      }
    >
      {label}
    </button>
  );
}

function SortableTh({ label, activeKey, dir, sortKey, onClick, align }: {
  label: string; activeKey: SortKey; dir: SortDir; sortKey: SortKey; onClick: (k: SortKey) => void; align?: 'right';
}) {
  const active = activeKey === sortKey;
  return (
    <th className={'px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider ' + (align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={
          'inline-flex items-center gap-1 select-none ' +
          (active ? 'text-uif-green-dark' : 'text-gray-600 hover:text-gray-900')
        }
        aria-label={`Sort by ${label}`}
      >
        {label}
        {active && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: number; accent?: 'red' | 'green' }) {
  const color =
    accent === 'red' ? 'text-red-700' :
    accent === 'green' ? 'text-uif-green-dark' :
    'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-1.5 mb-1.5">
        <DollarSign className="h-4 w-4 text-gray-400" />
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-600">{label}</div>
      </div>
      <div className={`text-xl font-bold font-mono ${color}`}>{money(value)}</div>
    </div>
  );
}
